import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { KycCase } from '../../data/types';
import { date } from '../../lib/format';
import { KYC_TYPE_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, StatusChip } from '../../ui/primitives';

const MATCH_FACTORS: Record<string, { label: string; score: number }[]> = {
  SANCTIONS_POTENTIAL: [
    { label: 'Name similarity', score: 91 },
    { label: 'Date of birth', score: 40 },
    { label: 'Nationality', score: 20 },
  ],
  PEP_MATCH: [
    { label: 'Name similarity', score: 98 },
    { label: 'Position held (local government)', score: 95 },
    { label: 'Location', score: 88 },
  ],
  ADVERSE_MEDIA: [
    { label: 'Name similarity', score: 84 },
    { label: 'Location', score: 30 },
    { label: 'Relevance to financial crime', score: 15 },
  ],
  ID_MISMATCH: [
    { label: 'Name', score: 100 },
    { label: 'Address', score: 100 },
    { label: 'Date of birth', score: 0 },
  ],
};

export default function KycCaseDrawer({
  kycCase,
  onClose,
}: {
  kycCase: KycCase | null;
  onClose: () => void;
}) {
  const { dispatch } = useRegistry();
  const scope = useTenantScope();
  const toast = useToast();
  const [resolution, setResolution] = useState('');
  const factors = kycCase ? MATCH_FACTORS[kycCase.type] : undefined;
  const holder = kycCase?.holderId ? scope.holderById.get(kycCase.holderId) : undefined;
  const update = (status: KycCase['status'], message: string, extra?: { assignedTo?: string }) => {
    if (!kycCase) return;
    dispatch({
      type: 'KYC_UPDATE',
      id: kycCase.id,
      status,
      resolution: resolution.trim() || undefined,
      ...extra,
    });
    toast(message);
    setResolution('');
    if (status === 'CLEARED' || status === 'ESCALATED') onClose();
  };
  return (
    <Drawer
      anchor="right"
      open={Boolean(kycCase)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
    >
      {kycCase && (
        <Box sx={{ p: 3 }}>
          <Typography variant="caption" color="text.secondary">
            {kycCase.id.toUpperCase()} · {kycCase.provider}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5 }}>
            {KYC_TYPE_LABELS[kycCase.type]}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <StatusChip
              status={kycCase.severity}
              label={`${kycCase.severity.toLowerCase()} severity`}
            />
            <StatusChip status={kycCase.status} />
          </Stack>
          <Alert severity={kycCase.severity === 'HIGH' ? 'error' : 'warning'} sx={{ mt: 2 }}>
            {kycCase.detail}
          </Alert>
          <Box sx={{ mt: 2 }}>
            <KeyValueGrid
              items={[
                { label: 'Subject', value: kycCase.subjectName },
                { label: 'Opened', value: date(kycCase.openedAt) },
                { label: 'Assigned to', value: kycCase.assignedTo ?? 'Unassigned' },
                {
                  label: 'Linked record',
                  value: holder ? (
                    <RouterLink to={`/manager/holders/sample/${holder.id}`} onClick={onClose}>
                      {holder.name}
                    </RouterLink>
                  ) : kycCase.applicationId ? (
                    <RouterLink
                      to={`/manager/applications/${kycCase.applicationId}`}
                      onClick={onClose}
                    >
                      Application
                    </RouterLink>
                  ) : (
                    '—'
                  ),
                },
              ]}
            />
          </Box>
          {factors && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Match factors
              </Typography>
              {factors.map((factor) => (
                <Box key={factor.label} sx={{ mb: 1 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">{factor.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {factor.score}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={factor.score}
                    color={factor.score > 80 ? 'error' : factor.score > 50 ? 'warning' : 'success'}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              ))}
            </>
          )}
          {kycCase.resolution && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Resolution: {kycCase.resolution}
            </Alert>
          )}
          {kycCase.status !== 'CLEARED' && (
            <>
              <Divider sx={{ my: 2 }} />
              <TextField
                label="Resolution notes"
                value={resolution}
                onChange={(event) => setResolution(event.target.value)}
                multiline
                minRows={3}
                fullWidth
                placeholder="e.g. Date of birth and nationality differ from the listed person; false positive."
              />
              <Stack
                useFlexGap
                direction="row"
                spacing={1}
                sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
              >
                <Button
                  variant="contained"
                  color="success"
                  disabled={!resolution.trim()}
                  onClick={() => update('CLEARED', 'Case cleared and holder status updated')}
                >
                  Clear
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    update('IN_REVIEW', 'Assigned to you', { assignedTo: scope.user.name })
                  }
                >
                  Assign to me
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    toast('Request for more information sent to the investor app', 'info')
                  }
                >
                  Request info
                </Button>
                <Button
                  color="error"
                  onClick={() =>
                    update('ESCALATED', 'Escalated to the AML compliance officer', {
                      assignedTo: 'Daniel Osei',
                    })
                  }
                >
                  Escalate
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
                Suspicious matter reports are lodged with AUSTRAC outside the platform; the case
                keeps the reference.
              </Typography>
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
}
